import React, { createContext, useContext, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useEntityList } from '../data/entities';
import { designLabel } from './InstanceLink';

// App focus (decision app-focus-context). The App Builder's `app` entity is
// the console's spine; the FOCUSED app is a sticky, explicit context — not
// a permission (the workspace is). One switcher (sidebar + module header),
// remembered per user per org, carried in the URL for deep links and for
// NoVA (`ctx.page` reads ?app=). Resolution: ?app= in the URL → the stored
// id, if it is still a visible app → nothing. There is NO "newest app"
// fallback: a module with no focus shows "All apps" (libraries) or a
// chooser (App Builder). Archived apps leave every picker.
//
// Library modules may widen the view to several apps (?apps=a,b — the
// focus set); Builder / Lab always act on exactly one.

const APP_KEY = (orgId) => `nv.app.${orgId}`;
// null = never chosen, '' = "All apps" chosen explicitly, else an app id.
const readStored = (orgId) => { try { return localStorage.getItem(APP_KEY(orgId)); } catch { return null; } };
const writeStored = (orgId, id) => { try { localStorage.setItem(APP_KEY(orgId), id || ''); } catch { /* ignore */ } };

const AppFocusContext = createContext({ apps: [], archived: [], app: null, loading: true, select: () => {}, orgId: null });

// Mounted once by OrgShell INSIDE TenantContext (the app list is
// workspace-bucketed, so it must read the live tenant selection).
export function AppFocusProvider({ orgId, children }) {
  const { docs } = useEntityList('app', { org: orgId });
  const [params, setParams] = useSearchParams();
  const wanted = params.get('app');
  const loading = docs === null;
  const apps = (docs || []).filter((a) => !a.archived);
  const archived = (docs || []).filter((a) => a.archived);
  const stored = readStored(orgId);
  const fromUrl = wanted ? apps.find((a) => a.id === wanted) || null : null;
  const fromStore = !wanted && stored ? apps.find((a) => a.id === stored) || null : null;
  const app = fromUrl || fromStore;

  useEffect(() => {
    if (loading) return;
    const next = new URLSearchParams(params);
    if (fromUrl) {
      // Arriving on a deep link makes that app the focus from here on.
      if (stored !== fromUrl.id) writeStored(orgId, fromUrl.id);
      return;
    }
    if (wanted) {
      // ?app= names something not visible here (archived, other
      // workspace, deleted): drop it so the sidebar stops forwarding it.
      next.delete('app'); next.delete('apps');
      setParams(next, { replace: true });
      return;
    }
    if (fromStore) {
      // Stored focus → URL, so every consumer (sidebar links, NoVA) sees it.
      next.set('app', fromStore.id);
      setParams(next, { replace: true });
      return;
    }
    // Stored id is stale (workspace switched, app archived): forget it.
    if (stored) writeStored(orgId, '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, wanted, fromUrl?.id, fromStore?.id, orgId]);

  const select = (id) => {
    const next = new URLSearchParams(params);
    if (id) next.set('app', id); else next.delete('app');
    next.delete('apps'); // a new primary resets the comparison set
    setParams(next, { replace: true });
    writeStored(orgId, id || '');
  };

  return (
    <AppFocusContext.Provider value={{ apps, archived, app, loading, select, orgId }}>
      {children}
    </AppFocusContext.Provider>
  );
}

// The focused app as every module reads it.
export function useSelectedApp() {
  return useContext(AppFocusContext);
}

// Library modules: the set of apps in view. null = all apps; otherwise the
// focused app plus any ?apps= companions that are still visible.
export function useAppFocusSet() {
  const { app, apps } = useSelectedApp();
  const [params, setParams] = useSearchParams();
  const extra = (params.get('apps') || '').split(',').filter((id) => id && apps.some((a) => a.id === id));
  const focus = app ? [...new Set([app.id, ...extra])] : null;
  const toggle = (id) => {
    if (!app || id === app.id) return;
    const set = extra.includes(id) ? extra.filter((x) => x !== id) : [...extra, id];
    const next = new URLSearchParams(params);
    if (set.length) next.set('apps', set.join(',')); else next.delete('apps');
    setParams(next, { replace: true });
  };
  return { focus, toggle };
}

// Does a record tagged with `appIds` fall inside the focus set?
export const inFocus = (focus, appIds) => !focus || (appIds || []).some((id) => focus.includes(id));

// Sidebar control: the one place the focus is chosen (OrgShell).
export function AppFocusSwitcher() {
  const { apps, app, select, loading } = useSelectedApp();
  return (
    <label className="pf-side-ws" title="App focus: every module narrows to this app; libraries can widen to several">
      <select
        className="cs-input cs-select"
        value={app?.id || ''}
        onChange={(e) => select(e.target.value || null)}
        aria-label="App focus"
        disabled={loading}
      >
        <option value="">{loading ? 'Loading…' : apps.length ? 'All apps' : 'No apps yet'}</option>
        {apps.map((a) => <option key={a.id} value={a.id}>{designLabel(a, apps)}</option>)}
      </select>
    </label>
  );
}

// Module-header control. Single mode (App Builder, DevOps): pick exactly
// one. allowNone (libraries that only act on the focus): "All apps" or the
// focus. multi (libraries that filter a list): allowNone plus a compare
// list that adds companions to ?apps=.
export function AppFocusChips({ multi = false, allowNone = multi }) {
  const { apps, app, select, loading, orgId } = useSelectedApp();
  const { focus, toggle } = useAppFocusSet();
  if (!loading && !apps.length) {
    return (
      <Link to={`/orgs/${orgId}/builder`} className="cs-btn cs-btn-outline cs-btn-sm">+ Define your first app</Link>
    );
  }
  const others = app ? apps.filter((a) => a.id !== app.id) : [];
  const extraCount = focus ? focus.length - 1 : 0;
  return (
    <>
      <label className="mf-muted" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        App
        <select className="cs-input" value={app?.id || ''} onChange={(e) => select(e.target.value || null)} aria-label="Select app" disabled={loading}>
          {allowNone ? <option value="">All apps</option> : <option value="">— choose an app —</option>}
          {apps.map((a) => <option key={a.id} value={a.id}>{designLabel(a, apps)}</option>)}
        </select>
      </label>
      {multi && app && others.length > 0 && (
        <details className="af-compare">
          <summary className={`mf-chip${extraCount ? ' is-info' : ''}`}>{extraCount ? `+${extraCount} more` : '+ compare'}</summary>
          <div className="af-compare-list" role="group" aria-label="Also show">
            {others.map((a) => (
              <label key={a.id} className="af-compare-row">
                <input type="checkbox" checked={!!focus?.includes(a.id)} onChange={() => toggle(a.id)} />
                {designLabel(a, apps)}
              </label>
            ))}
          </div>
        </details>
      )}
    </>
  );
}

// Keep the focus on module-to-module links (?app= and the ?apps= set).
export const withApp = (to, app) => {
  if (!app) return to;
  let q = `app=${app.id}`;
  try {
    const set = new URLSearchParams(window.location.search).get('apps');
    if (set) q += `&apps=${set}`;
  } catch { /* ignore */ }
  return `${to}${to.includes('?') ? '&' : '?'}${q}`;
};

export default AppFocusChips;
